/**
 * health/alerts.ts — pure, rule-based predictive health alerts.
 *
 * Deterministic and evidence-based: every alert derives from RECORDED data
 * (environmental readings with named sources, the user's own medication
 * refill dates and insurance expiry dates) and carries a non-empty basis
 * plus the medical disclaimer. Same inputs ⇒ same ids (once-only firing is
 * enforced by the module via persisted fired ids). No fabricated forecasts:
 * stale environmental readings (>48h) never alert; notices expire at 30 days.
 */

import {
  HEALTH_DISCLAIMER,
  type HealthAlert,
  type InsurancePolicyRef,
  type Medication,
  type TravelHealthReading,
} from './models';

export const EXTREME_HEAT_THRESHOLD_C = 40;
export const POOR_AQI_THRESHOLD = 150;
export const REFILL_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
export const INSURANCE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
const ENV_READING_MAX_AGE_MS = 48 * 60 * 60 * 1000;
const NOTICE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const dayBucket = (ms: number) => Math.floor(ms / (24 * 60 * 60 * 1000));

function latestReading(
  readings: TravelHealthReading[],
  metric: TravelHealthReading['metric'],
  city: string,
  now: number,
  maxAgeMs: number,
): TravelHealthReading | null {
  const fresh = readings.filter(
    (r) => r.metric === metric &&
      r.city.toLowerCase() === city.toLowerCase() &&
      now - r.recordedAt <= maxAgeMs,
  );
  if (fresh.length === 0) return null;
  return fresh.reduce((a, b) => (b.recordedAt > a.recordedAt ? b : a));
}

export interface HealthAlertInputs {
  currentCity: string | null;
  readings: TravelHealthReading[];
  medications: Medication[];
  policies: InsurancePolicyRef[];
  now: number;
}

export function computeHealthAlerts(inputs: HealthAlertInputs): HealthAlert[] {
  const { currentCity, readings, medications, policies, now } = inputs;
  const alerts: HealthAlert[] = [];
  const push = (id: string, kind: HealthAlert['kind'], message: string, basis: string[]) =>
    alerts.push({ id, kind, message, basis, disclaimer: HEALTH_DISCLAIMER });

  if (currentCity != null) {
    const cityKey = currentCity.toLowerCase();

    const heat = latestReading(readings, 'heatIndex', currentCity, now, ENV_READING_MAX_AGE_MS);
    if (heat?.value != null && heat.value >= EXTREME_HEAT_THRESHOLD_C) {
      push(
        `extremeHeat:${cityKey}:${dayBucket(heat.recordedAt)}`,
        'extremeHeat',
        `Extreme heat in ${currentCity} today (${heat.value}°C heat index). Stay hydrated and limit midday exposure.`,
        [`heat index ${heat.value}°C recorded by ${heat.source}`],
      );
    }

    const air = latestReading(readings, 'airQuality', currentCity, now, ENV_READING_MAX_AGE_MS);
    if (air?.value != null && air.value >= POOR_AQI_THRESHOLD) {
      push(
        `poorAirQuality:${cityKey}:${dayBucket(air.recordedAt)}`,
        'poorAirQuality',
        `Poor air quality in ${currentCity} (AQI ${air.value}). Consider limiting outdoor activity.`,
        [`AQI ${air.value} recorded by ${air.source}`],
      );
    }

    const vaccination = latestReading(readings, 'vaccinationGuidance', currentCity, now, NOTICE_MAX_AGE_MS);
    if (vaccination?.note != null) {
      push(
        `vaccinationInfo:${cityKey}:${dayBucket(vaccination.recordedAt)}`,
        'vaccinationInfo',
        `Vaccination guidance for ${currentCity}: ${vaccination.note}`,
        [`guidance recorded by ${vaccination.source}`],
      );
    }

    const seasonal = latestReading(readings, 'seasonalNotice', currentCity, now, NOTICE_MAX_AGE_MS);
    if (seasonal?.note != null) {
      push(
        `weatherHealthAdvisory:${cityKey}:${dayBucket(seasonal.recordedAt)}`,
        'weatherHealthAdvisory',
        `Seasonal health notice for ${currentCity}: ${seasonal.note}`,
        [`notice recorded by ${seasonal.source}`],
      );
    }
  }

  for (const med of medications) {
    if (med.refillDueAt == null) continue;
    const untilDue = med.refillDueAt - now;
    if (untilDue <= REFILL_WINDOW_MS) {
      const overdue = untilDue < 0;
      push(
        `medicationRefill:${med.id}:${dayBucket(med.refillDueAt)}`,
        'medicationRefill',
        overdue
          ? `Refill for ${med.name} is overdue.`
          : `Refill for ${med.name} is due within ${Math.max(1, Math.ceil(untilDue / (24 * 60 * 60 * 1000)))} day(s).`,
        [`refill date recorded for ${med.name}`],
      );
    }
  }

  for (const policy of policies) {
    if (policy.expiresAt == null) continue;
    const untilExpiry = policy.expiresAt - now;
    if (untilExpiry <= INSURANCE_WINDOW_MS) {
      push(
        `insuranceExpiration:${policy.id}:${dayBucket(policy.expiresAt)}`,
        'insuranceExpiration',
        untilExpiry < 0
          ? `${policy.provider} ${policy.kind} insurance has expired.`
          : `${policy.provider} ${policy.kind} insurance expires within ${Math.max(1, Math.ceil(untilExpiry / (24 * 60 * 60 * 1000)))} day(s).`,
        [`expiry date recorded for ${policy.provider} policy`],
      );
    }
  }

  return alerts;
}
